// Currency / number formatting helpers (en-IN with K/L/Cr abbreviation)

export function formatINRAbbrev(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  const v = Math.round(Number(n));
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2).replace(/\.00$/, "")} Cr`;
  if (Math.abs(v) >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(2).replace(/\.00$/, "")} L`;
  if (Math.abs(v) >= 1_000)       return `₹${(v / 1_000).toFixed(2).replace(/\.00$/, "")} K`;
  return `₹${v.toLocaleString("en-IN")}`;
}

export function formatINRFull(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  return `₹${Math.round(Number(n)).toLocaleString("en-IN")}`;
}

// Western abbreviation: K = thousand, M = million, B = billion.
export function formatINRWestern(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return `₹${(v / 1_000_000_000).toFixed(2).replace(/\.00$/, "")}B`;
  if (Math.abs(v) >= 1_000_000)     return `₹${(v / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (Math.abs(v) >= 1_000)         return `₹${(v / 1_000).toFixed(2).replace(/\.00$/, "")}K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

export function formatNumberAbbrev(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2).replace(/\.00$/, "")} Cr`;
  if (Math.abs(v) >= 1_00_000)    return `${(v / 1_00_000).toFixed(2).replace(/\.00$/, "")} L`;
  if (Math.abs(v) >= 1_000)       return `${(v / 1_000).toFixed(2).replace(/\.00$/, "")} K`;
  return `${Math.round(v).toLocaleString("en-IN")}`;
}

export function formatNumberFull(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("en-IN");
}

export function formatNumberWestern(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2).replace(/\.00$/, "")}B`;
  if (Math.abs(v) >= 1_000_000)     return `${(v / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (Math.abs(v) >= 1_000)         return `${(v / 1_000).toFixed(2).replace(/\.00$/, "")}K`;
  return `${Math.round(v).toLocaleString("en-IN")}`;
}

export function formatPercent(n, total) {
  if (!total || isNaN(Number(total))) return "0%";
  return `${Math.round((Number(n) / Number(total)) * 100)}%`;
}

export function formatByType(value, type) {
  if (value === undefined || value === null || value === "") return "—";
  if (type === "currency") return formatINRAbbrev(value);
  if (type === "currency_full") return formatINRFull(value);
  if (type === "currency_western") return formatINRWestern(value);
  if (type === "number") return formatNumberAbbrev(value);
  if (type === "number_full") return formatNumberFull(value);
  if (type === "number_western") return formatNumberWestern(value);
  if (type === "percentage") return `${value}%`;
  if (type === "date") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }
  return String(value);
}

export function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const suffix = (n) => {
    if (n % 100 >= 11 && n % 100 <= 13) return "th";
    switch (n % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
  };
  const month = d.toLocaleDateString("en-IN", { month: "short" });
  return `${day}${suffix(day)} ${month}`;
}

export function formatDateRange(startIso, endIso) {
  if (!startIso && !endIso) return "—";
  if (startIso && !endIso) return formatDateShort(startIso);
  if (!startIso && endIso) return formatDateShort(endIso);
  return `${formatDateShort(startIso)} – ${formatDateShort(endIso)}`;
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = (Date.now() - d.getTime()) / 1000;
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export function aggregate(rows, column, agg, filterValue) {
  if (!rows || !rows.length) return 0;
  const values = rows
    .filter((r) => filterValue == null || filterValue === "" || String(r[column]) === String(filterValue))
    .map((r) => r[column]);
  if (agg === "COUNT") return values.length;
  const nums = values.map((v) => Number(String(v).replace(/[₹,\s]/g, ""))).filter((n) => !isNaN(n));
  if (!nums.length) return 0;
  if (agg === "SUM") return nums.reduce((a, b) => a + b, 0);
  if (agg === "AVG") return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === "MAX") return Math.max(...nums);
  if (agg === "MIN") return Math.min(...nums);
  return nums.length ? nums[0] : 0;
}

export function colorFromString(str) {
  const palette = [
    ["#fef2e9", "#c2410c"],
    ["#eff6ff", "#1d4ed8"],
    ["#f0fdf4", "#15803d"],
    ["#fdf4ff", "#a21caf"],
    ["#fef3c7", "#92400e"],
    ["#ecfeff", "#0e7490"],
    ["#fef2f2", "#b91c1c"],
    ["#f1f5f9", "#334155"],
  ];
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

export function initials(text) {
  if (!text) return "?";
  const parts = String(text).split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
